import Cookies from "universal-cookie";
import { v4 as uuidv4 } from 'uuid';
import { hasConsentForCategory } from './cookieConsent';
import { setSecureCookieWithConsent } from './cookies';

const ANALYTICS_SESSION_KEY = 'analytics_session_id';
const ANALYTICS_CONSENT_KEY = 'analytics_consent';
const SESSION_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

/**
 * Generates or retrieves an anonymous session ID for analytics
 * This ID is not linked to the user's account and is GDPR-compliant
 */
export function getAnonymousSessionId() {
  const cookies = new Cookies();
  let sessionId = cookies.get(ANALYTICS_SESSION_KEY);
  
  if (!sessionId) {
    sessionId = uuidv4();
    setSecureCookieWithConsent(ANALYTICS_SESSION_KEY, sessionId, { maxAge: SESSION_DURATION / 1000 }, (cookieName) => hasConsentForCategory('analytics'));
  }
  
  return sessionId;
}

/**
 * Checks if user has consented to analytics
 */
export function hasAnalyticsConsent() {
  // Use the granular consent system
  return hasConsentForCategory('analytics');
}

/**
 * Sets analytics consent
 */
export function setAnalyticsConsent(consent) {
  const cookies = new Cookies();
  setSecureCookieWithConsent(ANALYTICS_CONSENT_KEY, consent, { maxAge: 365 * 24 * 60 * 60 }, (cookieName) => hasConsentForCategory('analytics'));
}

/**
 * Gets a pseudonymized user identifier if consent is given
 * Now uses backend endpoint for secure pseudonymization
 */
export async function getPseudonymizedUserId(userId) {
  if (!userId || !hasAnalyticsConsent()) {
    return null;
  }
  
  try {
    // Import here to avoid circular dependency
    const { getToken } = await import('../Requests/User/UserRequests');
    const token = getToken();
    
    if (!token) {
      console.warn('No authentication token available for analytics pseudonymization');
      return null;
    }
    
    const sessionId = getAnonymousSessionId();
    
    const response = await fetch(process.env.REACT_APP_API_BASE_URL + '/analytics/pseudonymize', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId: userId,
        sessionId: sessionId
      })
    });
    
    if (!response.ok) {
      if (response.status === 422) {
        const errorData = await response.json();
        console.error('Validation error during pseudonymization:', errorData);
      } else {
        console.error('Failed to pseudonymize user ID:', response.status, response.statusText);
      }
      return null;
    }
    
    const data = await response.json();
    
    // Validate response structure
    if (!data.pseudonymizedId) {
      console.error('Invalid response from pseudonymization endpoint:', data);
      return null;
    }
    
    return data.pseudonymizedId;
    
  } catch (error) {
    console.error('Error during user ID pseudonymization:', error);
    return null;
  }
}

/**
 * Clears all analytics-related data
 */
export function clearAnalyticsData() {
  const cookies = new Cookies();
  cookies.remove(ANALYTICS_SESSION_KEY, { path: '/' });
  cookies.remove(ANALYTICS_CONSENT_KEY, { path: '/' });
}